import yaml from 'js-yaml'
import semver from 'semver'
import { sanitizeFileName } from './utils'
import { getDocHost, selectDocVersion, releaseBuild } from '@/services/docsite'

const buildVersion = import.meta.env.VITE_BUILD_VERSION
const docsiteOverride = useRoute()?.query?.docsite
const docsite = getDocHost(docsiteOverride)

const latestVersion = shallowRef<string>('latest')
const searchIndexData = shallowRef<string>('')
const docIndexData = shallowRef<Record<string, any>>({})
const siteIndexData = shallowRef<Record<string, any>>({})
let loaded: Promise<any>|null = null
const fetchOptions: Record<string, any> = !docsite.match(/tester/)
  ? {}
  : {
      method: 'GET',
      headers: {
        Authorization: `Basic ${btoa('ybdocs:YBDmgmt')}`
      },
      mode: 'cors',
      credentials: 'include'
    }

const load = async () => {

  // Get the versions.yaml to get the doc version to load.
  const versionsResponse = await fetch(`${docsite}/versions.yaml`)
  const versionsText = await versionsResponse.text()
  const versions = yaml.loadAll(versionsText)[0]
    .map((obj: any) => obj.version)
    .filter(semver.valid)
    .sort(semver.rcompare)

  // Release builds (Option B): pin to the highest published patch in the
  // build's own minor line so a release build can never cross-link to an
  // unrelated minor (e.g. 7.6.0). Dev/unparseable builds preserve the legacy
  // global-newest pick so local ?docsite= preview is unchanged.
  latestVersion.value = releaseBuild
    ? selectDocVersion(versions, buildVersion)
    : versions[0]

  // Get the search index.
  const searchIndexResponse = await fetch(`${docsite}/${latestVersion.value}/searchindex.json`)
  searchIndexData.value = await searchIndexResponse.text()

  // Get the doc hash index.
  const indexResponse = await fetch(`${docsite}/${latestVersion.value}/index.html`, fetchOptions)
  const indexText = await indexResponse.text()
  const index = indexText.split('\n').find(line => line.includes('__VP_HASH_MAP__') && line.includes('__VP_SITE_DATA__'))
  if (index) {
    // Split the index by first semicolon.
    const semicolonIndex = index.indexOf(';')
    if (semicolonIndex > 0) {

      // Extract the doc hash index.
      const hashMapIndex = index.substring(0, semicolonIndex)
      {
        const firstQuoteIndex = hashMapIndex.indexOf('"')
        const lastQuoteIndex = hashMapIndex.lastIndexOf('"')
        const indexData = hashMapIndex?.substring(firstQuoteIndex + 1, lastQuoteIndex)
        docIndexData.value = JSON.parse(JSON.parse('"' + indexData + '"'))
      }

      // Extract the site data.
      const siteDataIndex = index.substring(semicolonIndex + 1)
      {
        const firstQuoteIndex = siteDataIndex.indexOf('"')
        const lastQuoteIndex = siteDataIndex.lastIndexOf('"')
        const indexData = siteDataIndex?.substring(firstQuoteIndex + 1, lastQuoteIndex)
        siteIndexData.value = JSON.parse(JSON.parse('"' + indexData + '"'))
      }
    }
  }
}

const pathToTarget = (path: string) => {
  return `${docsite}${path}`
}

const pathToFile = (path: string) => {
  let pagePath = path.replace(/\.html$/, '')
  pagePath = decodeURIComponent(pagePath)
  pagePath = pagePath.replace(/\/$/, '/index') // /foo/ -> /foo/index

  // in production, each .md file is built into a .md.js file following
  // the path conversion scheme.
  // /7.3.0/foo/bar.html -> ./foo_bar.md
  const base = '/' + latestVersion.value + '/'
  pagePath =
      sanitizeFileName(
        pagePath.slice(base.length).replace(/\//g, '_') || 'index'
      ) + '.md'
  // client production build needs to account for page hash, which is
  // injected directly in the page's html
  let pageHash = docIndexData.value[pagePath.toLowerCase()]
  if (!pageHash) {
    pagePath = pagePath.endsWith('_index.md')
      ? pagePath.slice(0, -9) + '.md'
      : pagePath.slice(0, -3) + '_index.md'
    pageHash = docIndexData.value[pagePath.toLowerCase()]
  }
  if (!pageHash) { return null }
  pagePath = `${docsite}/${latestVersion.value}/assets/${pagePath}.${pageHash}.js`

  return pagePath
}

const useSearchIndex = () => {
  if (!loaded) {
    loaded = load()
  }
  return {
    searchIndexData,
    docIndexData,
    siteIndexData,
    latestVersion,
    docsite,
    pathToFile,
    pathToTarget,
    loaded,
    fetchOptions
  }
}
export default useSearchIndex
